import React, { useState } from "react";
import Header from "../components/Header";
import { orderService } from "../services/apiService";
import { useProfile } from "../utils/UseProfile";

const TYPE_OPTIONS = [
  { label: "All", value: "ALL" },
  { label: "Sale", value: "CONFIRMED" },
  { label: "Partial Return", value: "PARTIAL_RETURN" },
  { label: "Return", value: "RETURNED" },
];

const TYPE_LABEL = { CONFIRMED: "Sale", PARTIAL_RETURN: "Partial Return", RETURNED: "Return" };
const TYPE_BADGE = {
  CONFIRMED: "bg-green-100 text-green-700 border border-green-300",
  PARTIAL_RETURN: "bg-orange-100 text-orange-700 border border-orange-300",
  RETURNED: "bg-red-100 text-red-700 border border-red-300",
};

const formatDate = (iso) => {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const fmt = (val) => val != null ? `₹${Number(val).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-";

const SS_KEY = "sells_search";
const getSession = () => { try { return JSON.parse(sessionStorage.getItem(SS_KEY)) || {}; } catch { return {}; } };
const saveSession = (patch) => {
  const token = localStorage.getItem("token") || "";
  try { sessionStorage.setItem(SS_KEY, JSON.stringify({ ...getSession(), _token: token, ...patch })); } catch {}
};

const SortIcon = ({ col, sortConfig }) => {
  if (!sortConfig || sortConfig.key !== col) return <i className="fas fa-sort text-gray-300 ml-1 text-xs" />;
  return sortConfig.dir === "asc" ? <i className="fas fa-sort-up text-indigo-500 ml-1 text-xs" /> : <i className="fas fa-sort-down text-indigo-500 ml-1 text-xs" />;
};

const Th = ({ col, label, className = "text-left", sortConfig, onSort }) => (
  <th className={`py-3 px-4 ${className} cursor-pointer select-none hover:bg-blue-100`} onClick={() => onSort(col)}>
    {label}<SortIcon col={col} sortConfig={sortConfig} />
  </th>
);

const Sell = () => {
  const { profile } = useProfile();
  const role = profile?.roleResponse?.role;
  const isAdmin = Array.isArray(role)
    ? role.some((r) => r.toUpperCase().includes("ADMIN"))
    : typeof role === "string" && role.toUpperCase().includes("ADMIN");
  const currentToken = localStorage.getItem("token") || "";
  const saved = getSession();
  const sessionValid = saved._token === currentToken;

  const [filters, setFilters] = useState(() => sessionValid
    ? { dateFrom: saved.dateFrom || "", dateTo: saved.dateTo || "", mobile: saved.mobile || "", orderType: saved.orderType || "ALL" }
    : { dateFrom: "", dateTo: "", mobile: "", orderType: "ALL" }
  );
  const [results, setResults] = useState(() => sessionValid ? (saved.results || []) : []);
  const [searchPerformed, setSearchPerformed] = useState(() => sessionValid ? (saved.searchPerformed || false) : false);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(() => sessionValid ? (saved.currentPage || 0) : 0);
  const [totalPages, setTotalPages] = useState(() => sessionValid ? (saved.totalPages || 0) : 0);
  const [totalRecords, setTotalRecords] = useState(() => sessionValid ? (saved.totalRecords || 0) : 0);
  const [summary, setSummary] = useState(() => sessionValid ? (saved.summary || null) : null);
  const pageSize = 15;
  const [sortConfig, setSortConfig] = useState({ key: null, dir: "asc" });
  const handleSort = (key) => setSortConfig((prev) => ({ key, dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc" }));

  const handleChange = (e) => {
    const updated = { ...filters, [e.target.name]: e.target.value };
    setFilters(updated);
    saveSession({ dateFrom: updated.dateFrom, dateTo: updated.dateTo, mobile: updated.mobile, orderType: updated.orderType });
  };

  const handleTypeChange = (value) => {
    const updated = { ...filters, orderType: value };
    setFilters(updated);
    saveSession({ ...updated });
  };

  const fetchPage = async (page, currentFilters) => {
    setLoading(true);
    try {
      const params = { page, size: pageSize };
      if (currentFilters.dateFrom) params.fromDate = currentFilters.dateFrom;
      if (currentFilters.dateTo)   params.toDate   = currentFilters.dateTo;
      if (currentFilters.mobile)   params.customerMobile = currentFilters.mobile;
      if (currentFilters.orderType && currentFilters.orderType !== "ALL") params.orderStatus = currentFilters.orderType;
      const data = await orderService.searchOrders(params);
      const list = Array.isArray(data) ? data : (data?.content ?? []);
      const tp = data?.totalPages ?? (Array.isArray(data) ? 1 : 0);
      const tr = data?.totalElements ?? list.length;
      const sm = data?.summary ?? null;
      setResults(list);
      setCurrentPage(page);
      setTotalPages(tp);
      setTotalRecords(tr);
      setSummary(sm);
      setSearchPerformed(true);
      saveSession({ results: list, currentPage: page, totalPages: tp, totalRecords: tr, summary: sm, searchPerformed: true });
    } catch {
      setResults([]);
      setSearchPerformed(true);
      saveSession({ results: [], searchPerformed: true });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => fetchPage(0, filters);

  const paginatedResults = [...results].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aVal = a[sortConfig.key] ?? "";
    const bVal = b[sortConfig.key] ?? "";
    const cmp = typeof aVal === "number" && typeof bVal === "number" ? aVal - bVal : String(aVal).localeCompare(String(bVal));
    return sortConfig.dir === "asc" ? cmp : -cmp;
  });

  return (
    <div className="h-full flex flex-col overflow-hidden bg-gradient-to-tr from-blue-50 via-indigo-50 to-teal-50 p-3 sm:p-6">
      <Header showSearch={false} showCart={false} />

      {/* Filter Section */}
      <section className="relative rounded-xl overflow-hidden shadow-md border border-blue-200 mt-4 flex-shrink-0">
        <h2 className="text-left text-sm font-bold text-blue-800 px-4 py-2 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200">
          🔍 Search Sells
        </h2>
        <div className="p-2">
          <div className="flex flex-wrap gap-2 items-end">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Sell From</label>
              <input type="date" id="dateFrom" name="dateFrom" value={filters.dateFrom} onChange={handleChange}
                className="w-44 px-3 py-1 rounded-lg border border-blue-300 focus:ring-2 focus:ring-blue-400 focus:outline-none text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Sales To</label>
              <input type="date" id="dateTo" name="dateTo" value={filters.dateTo} onChange={handleChange}
                className="w-44 px-3 py-1 rounded-lg border border-blue-300 focus:ring-2 focus:ring-blue-400 focus:outline-none text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Mobile No</label>
              <input type="text" id="mobile" name="mobile" value={filters.mobile} onChange={handleChange}
                maxLength={10} placeholder="Enter number"
                className="w-44 px-3 py-1 rounded-lg border border-blue-300 focus:ring-2 focus:ring-blue-400 focus:outline-none text-sm" />
            </div>
            {/* Type Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
              <select
                name="orderType"
                value={filters.orderType}
                onChange={(e) => handleTypeChange(e.target.value)}
                className="w-44 px-3 py-1 rounded-lg border border-blue-300 focus:ring-2 focus:ring-blue-400 focus:outline-none text-sm bg-white"
              >
                {TYPE_OPTIONS.map(({ label, value }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end mt-3">
            <button onClick={handleSearch} disabled={loading}
              className={`flex items-center justify-center gap-1.5 px-4 py-1.5 rounded-full text-white font-semibold shadow text-xs transition ${
                loading ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-indigo-600 hover:to-blue-600"
              }`}>
              {loading ? <><i className="fas fa-spinner fa-spin" />Searching...</> : <><i className="fas fa-search" />Search</>}
            </button>
          </div>
        </div>
      </section>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-4 gap-2 mt-2 flex-shrink-0">
          <div className="bg-white rounded-xl border border-green-200 shadow-sm px-4 py-2.5 flex flex-col">
            <span className="text-xs text-gray-500">Sales Orders</span>
            <span className="text-xl font-bold text-green-600">{summary.salesCount ?? 0}</span>
          </div>
          <div className="bg-white rounded-xl border border-red-200 shadow-sm px-4 py-2.5 flex flex-col">
            <span className="text-xs text-gray-500">Returned Orders</span>
            <span className="text-xl font-bold text-red-500">{summary.returnCount ?? 0}</span>
          </div>
          <div className="bg-white rounded-xl border border-orange-200 shadow-sm px-4 py-2.5 flex flex-col">
            <span className="text-xs text-gray-500">Partial Returned</span>
            <span className="text-xl font-bold text-orange-500">{summary.partialReturnCount ?? 0}</span>
          </div>
          <div className="bg-white rounded-xl border border-indigo-200 shadow-sm px-4 py-2.5 flex flex-col">
            <span className="text-xs text-gray-500">Net Sales</span>
            <span className="text-xl font-bold text-indigo-600">₹{(summary.netSales ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
          </div>
        </div>
      )}

      {/* Results Section */}
      <section className="relative mt-2 rounded-xl overflow-hidden shadow-md border border-blue-200 flex flex-col flex-1 min-h-0">
        <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200 flex-shrink-0">
          <h2 className="text-base font-bold text-blue-800">
            📋 Results
            {totalRecords > 0 && <span className="text-xs font-normal text-blue-600 ml-2">({totalRecords} record{totalRecords !== 1 ? "s" : ""})</span>}
          </h2>
        </div>
        <div className="overflow-x-auto flex-1">
        <table className="min-w-full text-sm text-gray-800 border border-blue-200 rounded-lg overflow-hidden">
          <thead className="bg-gradient-to-r from-blue-200 via-indigo-100 to-teal-100 text-blue-900 uppercase text-xs tracking-wide">
            <tr>
              <Th col="billNo" label="Invoice" sortConfig={sortConfig} onSort={handleSort} />
              <Th col="customerMobile" label="Mobile" sortConfig={sortConfig} onSort={handleSort} />
              <Th col="orderStatus" label="Type" sortConfig={sortConfig} onSort={handleSort} />
              <Th col="finalAmount" label="Gross Sale" className="text-right" sortConfig={sortConfig} onSort={handleSort} />
              <Th col="returnAmount" label="Return" className="text-right" sortConfig={sortConfig} onSort={handleSort} />
              <Th col="netSale" label="Net Sale" className="text-right" sortConfig={sortConfig} onSort={handleSort} />
              {isAdmin && <Th col="netProfit" label="Profit" className="text-right" sortConfig={sortConfig} onSort={handleSort} />}
            </tr>
          </thead>
          <tbody>
            {paginatedResults.length === 0 ? (
              <tr>
                <td colSpan={isAdmin ? 7 : 6} className="text-center py-6 text-gray-500 italic">
                  {searchPerformed ? "No matching sales found. Try adjusting filters." : "No sell records to display. Please use 🔍 Search to begin."}
                </td>
              </tr>
            ) : (
              paginatedResults.map((record, index) => (
                <tr key={record.orderId}
                  className={`border-t ${index % 2 === 0 ? "bg-white" : "bg-blue-50"} hover:bg-indigo-50 transition-colors`}>
                  <td className="py-2 px-4">
                    <div className="font-medium text-indigo-700">{record.billNo ?? "-"}</div>
                    <div className="text-xs text-gray-400">{formatDate(record.createdOn)}</div>
                  </td>
                  <td className="py-2 px-4">{record.customerMobile ?? "-"}</td>
                  <td className="py-2 px-4">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_BADGE[record.orderStatus] ?? "bg-gray-100 text-gray-600 border border-gray-300"}`}>
                      {TYPE_LABEL[record.orderStatus] ?? record.orderStatus ?? "-"}
                    </span>
                  </td>
                  <td className="py-2 px-4 text-right">{fmt(record.finalAmount)}</td>
                  <td className="py-2 px-4 text-right text-red-500">{record.returnAmount > 0 ? fmt(record.returnAmount) : "-"}</td>
                  <td className="py-2 px-4 text-right font-semibold text-green-700">{fmt(record.netSale)}</td>
                  {isAdmin && <td className="py-2 px-4 text-right text-teal-700">{fmt(record.netProfit)}</td>}
                </tr>
              ))
            )}
          </tbody>
        </table>

        </div>

        {totalPages > 1 && (
          <div className="flex justify-between items-center px-5 py-2 border-t border-gray-100 bg-white flex-shrink-0">
            <span className="text-xs text-gray-500">
              Showing {currentPage * pageSize + 1}–{Math.min((currentPage + 1) * pageSize, totalRecords)} of {totalRecords}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => fetchPage(0, filters)} disabled={currentPage === 0}
                className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">«</button>
              <button onClick={() => fetchPage(currentPage - 1, filters)} disabled={currentPage === 0}
                className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">‹</button>
              {Array.from({ length: totalPages }, (_, i) => i)
                .slice(Math.max(0, currentPage - 2), currentPage + 3)
                .map((i) => (
                  <button key={i} onClick={() => fetchPage(i, filters)}
                    className={`px-2 py-1 text-xs rounded border ${
                      i === currentPage ? "bg-indigo-500 text-white border-indigo-500" : "border-gray-200 hover:bg-gray-50"
                    }`}>{i + 1}</button>
                ))}
              <button onClick={() => fetchPage(currentPage + 1, filters)} disabled={currentPage >= totalPages - 1}
                className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">›</button>
              <button onClick={() => fetchPage(totalPages - 1, filters)} disabled={currentPage >= totalPages - 1}
                className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">»</button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default Sell;
